import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";
import type { z } from "zod";

import type { MotivoIndisponibilidadeHorario } from "@repo/graphql-types/graphql";

import { RecurrenceFrequency } from "@/lib/enums/recurrence-frequency";
import type { patientInfo } from "@/lib/form-schemas/appointment-schema";
import type { recurringAppointmentBasicInfo } from "@/lib/form-schemas/recurring-appointment-schema";
import { getCurrentDate } from "@/lib/helpers/date";

type RecurringAppointmentForm = z.infer<typeof recurringAppointmentBasicInfo>;
type RecurringPatient = z.infer<typeof patientInfo>;

type RecurringSessionStatus = "active" | "removedByUser" | "unavailable";

type RecurringSessionResolution =
  | "consultorioVirtual"
  | "closureIgnored"
  | "skipped"
  | "kept";

type MotivoIndisponibilidade = `${MotivoIndisponibilidadeHorario}`;

interface RecurringSession {
  /** Data da sessão no formato yyyy-MM-dd, sem hora. */
  data: string;
  status: RecurringSessionStatus;
  resolution?: RecurringSessionResolution;
  /** Override de unidade só desta sessão; hoje só o Consultório Virtual. */
  codUnidade?: number;
  motivoIndisponibilidade?: MotivoIndisponibilidade;
}

const recurringAppointmentFormAtom = atomWithReset<RecurringAppointmentForm>({
  unit: 0,
  appointmentType: 0,
  paymentMethod: 0,
  startDate: getCurrentDate(),
  frequency: RecurrenceFrequency.Weekly,
  unitName: "",
  unitAcronym: "",
  appointmentTypeName: "",
});

const recurringSlotAtom = atomWithReset({
  horaInicio: "",
  horaFim: "",
  /** Preenchido a partir da resposta de horariosRecorrentes. */
  minimoAgendamentos: 0,
});

const recurringSessionsAtom = atomWithReset<RecurringSession[]>([]);

interface RecurringPatientCap {
  disponivel: number;
  limite: number;
}

const recurringPatientCapAtom = atomWithReset<RecurringPatientCap | null>(null);

/** Índice do histórico da tela que abriu o fluxo; o voltar da listagem usa para reposicionar até a origem. */
const recurringFlowOriginIndexAtom = atom<number | null>(null);

const recurringPatientAtom = atomWithReset<RecurringPatient>({
  patientId: undefined,
  telefoneInternacionalNormalizado: null,
  phone: "",
  phoneCountry: undefined,
  name: "",
  dateOfBirth: undefined,
  email: "",
  cpf: undefined,
  sendConfirmationViaWhatsApp: true,
  roomTypes: [],
});

export type {
  MotivoIndisponibilidade,
  RecurringPatientCap,
  RecurringSession,
  RecurringSessionResolution,
  RecurringSessionStatus,
};
export {
  recurringAppointmentFormAtom,
  recurringFlowOriginIndexAtom,
  recurringPatientAtom,
  recurringPatientCapAtom,
  recurringSessionsAtom,
  recurringSlotAtom,
};
